import type { ClientEmail as ClientEmailT, MeetingSummary } from "@scoach/types";
import { useToast } from "@scoach/ui";
import { Alert, Check, Copy, Send, Spark } from "@scoach/ui/icons";
import { useEffect, useMemo, useState } from "react";

import { summaryApi } from "../api.ts";

export interface ClientEmailProps {
  summary: MeetingSummary;
  onSummaryChange: (s: MeetingSummary) => void;
  /** When previewing an older version, never regenerate/save onto the live doc. */
  readOnly?: boolean;
}

function flatten(e: ClientEmailT): string {
  if (e.bodyText) return e.bodyText;
  return [e.greeting, ...(e.body ?? []), e.signoff].filter(Boolean).join("\n\n");
}

/** Which content sections the user wants assembled into the email. */
interface Sections {
  summary: boolean;
  pointToAction: boolean;
  internalQuestions: boolean;
  links: boolean;
  customQuestions: boolean;
}

const SECTION_LABELS: { key: keyof Sections; label: string; hint: string }[] = [
  { key: "summary", label: "Summary", hint: "General summary of the meeting." },
  { key: "pointToAction", label: "Point to Action", hint: "Operational action items to execute." },
  { key: "internalQuestions", label: "Internal Questions", hint: "Internal questions that were raised." },
  { key: "links", label: "Links", hint: "Links to additional training or reference materials." },
  { key: "customQuestions", label: "Custom Questions", hint: "Personalized questions for the customer." },
];

/** Build the email body from the selected structured summary sections, wrapped in
 * the generated greeting + sign-off. Used by the section checkboxes to let the
 * user dynamically assemble what the email contains. */
function assembleBody(summary: MeetingSummary, sections: Sections): string {
  const e = summary.client;
  const internal = summary.internal;
  const parts: string[] = [];
  if (e.greeting) parts.push(e.greeting);

  if (sections.summary) {
    const text = internal.overview?.summary?.trim() || (e.body ?? []).join("\n\n");
    if (text) parts.push(`**Summary**\n${text}`);
  }
  if (sections.pointToAction && internal.actionItems?.length) {
    const bullets = internal.actionItems
      .map((a) => {
        const meta = [a.who, a.due ? new Date(a.due).toLocaleDateString() : ""]
          .filter(Boolean)
          .join(", ");
        return `- ${a.what}${meta ? ` (${meta})` : ""}`;
      })
      .join("\n");
    parts.push(`**Point to action**\n${bullets}`);
  }
  if (sections.internalQuestions && internal.questionsAsked?.length) {
    const bullets = internal.questionsAsked.map((q) => `- ${q.q}`).join("\n");
    parts.push(`**Open questions**\n${bullets}`);
  }
  if (sections.links) {
    const refs = summary.references?.length ? summary.references : (e.references ?? []);
    if (refs.length) {
      const bullets = refs.map((r) => `- ${r.title}: ${r.href}`).join("\n");
      parts.push(`**Links**\n${bullets}`);
    }
  }
  if (sections.customQuestions && internal.customSummary?.length) {
    const bullets = internal.customSummary
      .filter((c) => c.answer && c.source !== "not-found")
      .map((c) => `- ${c.question} ${c.answer}`)
      .join("\n");
    if (bullets) parts.push(`**Questions for you**\n${bullets}`);
  }

  if (e.signoff) parts.push(e.signoff);
  return parts.filter(Boolean).join("\n\n");
}

export function ClientEmail({ summary, onSummaryChange, readOnly = false }: ClientEmailProps) {
  const e = summary.client;
  const initial = useMemo(() => flatten(e), [e]);
  const [tone, setTone] = useState<typeof e.tone>(e.tone ?? "warm");
  const [bodyText, setBodyText] = useState(initial);
  const [subject, setSubject] = useState(e.subject ?? "");
  const [to, setTo] = useState("");
  const [sections, setSections] = useState<Sections>({
    summary: true,
    pointToAction: true,
    internalQuestions: false,
    links: true,
    customQuestions: false,
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const toast = useToast();

  // Re-sync local state when the parent supplies a fresh summary (e.g. after
  // tone regeneration). Drops the dirty flag because the new server payload is
  // the new source of truth.
  useEffect(() => {
    setBodyText(initial);
    setSubject(e.subject ?? "");
    setTone(e.tone ?? "warm");
    setDirty(false);
  }, [initial, e.subject, e.tone]);

  function toggleSection(key: keyof Sections) {
    const next = { ...sections, [key]: !sections[key] };
    setSections(next);
    setBodyText(assembleBody(summary, next));
    setDirty(true);
  }

  function confirmDiscardEdits(): boolean {
    if (!dirty && !e.edited) return true;
    return window.confirm(
      "You've edited this email. Regenerating will discard your edits. Continue?",
    );
  }

  async function changeTone(next: typeof tone) {
    if (readOnly) return;
    if (next === tone) return;
    if (!confirmDiscardEdits()) return;
    setTone(next);
    setRegenerating(true);
    try {
      const fresh = await summaryApi.regenerateEmail(summary.meetingId, { tone: next });
      onSummaryChange({ ...summary, client: fresh });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setRegenerating(false);
    }
  }

  async function regenerate() {
    if (readOnly) return;
    if (!confirmDiscardEdits()) return;
    setRegenerating(true);
    try {
      const fresh = await summaryApi.regenerateEmail(summary.meetingId, { tone });
      onSummaryChange({ ...summary, client: fresh });
      toast.push({ tone: "success", message: "Email regenerated" });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setRegenerating(false);
    }
  }

  async function save() {
    if (readOnly) return;
    setSaving(true);
    try {
      const fresh = await summaryApi.updateEmail(summary.meetingId, { bodyText, subject });
      onSummaryChange({ ...summary, client: fresh });
      setDirty(false);
      toast.push({ tone: "success", message: "Email saved" });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  function copySummary() {
    void navigator.clipboard.writeText(bodyText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }

  /** Hand off to the user's native Gmail compose, pre-filled with the subject +
   * body (and recipients if entered). Opens in a new tab so the summary stays put. */
  function sendViaGmail() {
    const url =
      "https://mail.google.com/mail/?view=cm&fs=1" +
      (to.trim() ? `&to=${encodeURIComponent(to.trim())}` : "") +
      `&su=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(bodyText)}`;
    // Gmail's compose URL is capped (~2KB of URL is safe across browsers). Very
    // long emails can be truncated by the browser — warn but still open, since
    // Copy remains a full-fidelity fallback.
    if (url.length > 8000) {
      toast.push({
        tone: "warn",
        message: "Email is long — Gmail may truncate it. Use Copy if anything is missing.",
      });
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="email-wrap">
      <div className="email-controls">
        <div className="email-controls-left">
          <span className="email-kicker">TONE</span>
          <div className="seg seg-sm" style={regenerating ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
            <button type="button" className={tone === "formal" ? "on" : ""} disabled={regenerating} onClick={() => void changeTone("formal")}>Formal</button>
            <button type="button" className={tone === "warm" ? "on" : ""} disabled={regenerating} onClick={() => void changeTone("warm")}>Warm</button>
            <button type="button" className={tone === "brief" ? "on" : ""} disabled={regenerating} onClick={() => void changeTone("brief")}>Brief</button>
          </div>
          <button type="button" className="ghost-btn" onClick={regenerate} disabled={regenerating}>
            <Spark size={14} /> {regenerating ? "Regenerating…" : "Regenerate"}
          </button>
          {e.edited && !dirty && (
            <span className="email-edited-pill" title={e.editedAt ? `Edited ${new Date(e.editedAt).toLocaleString()}` : undefined}>
              Manually edited
            </span>
          )}
        </div>
        <div className="email-controls-right">
          <button
            type="button"
            className="pill-btn"
            onClick={() => void save()}
            disabled={!dirty || saving}
          >
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
          <button type="button" className={`pill-btn ${copied ? "sent" : ""}`} onClick={copySummary}>
            {copied ? (
              <>
                <Check size={14} /> Copied
              </>
            ) : (
              <>
                <Copy size={14} /> Copy
              </>
            )}
          </button>
          <button type="button" className="pill-btn primary" onClick={sendViaGmail}>
            <Send size={14} /> Send
          </button>
        </div>
      </div>

      <div className="email-sections">
        <span className="email-kicker">INCLUDE</span>
        {SECTION_LABELS.map((s) => (
          <label key={s.key} className="email-section-check" title={s.hint}>
            <input
              type="checkbox"
              checked={sections[s.key]}
              onChange={() => toggleSection(s.key)}
            />
            {s.label}
          </label>
        ))}
      </div>

      <div className="email-card" style={{ position: "relative" }}>
        {regenerating && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              background: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(1px)",
              borderRadius: "inherit",
              fontSize: 14,
              fontWeight: 600,
              color: "var(--text-2)",
            }}
          >
            <span className="dot dot-pulse" style={{ background: "var(--gc-blue)" }} />
            Updating email…
          </div>
        )}
        <div className="email-head">
          <div className="email-row">
            <span className="email-lbl">To</span>
            <div className="email-chips">
              <input
                className="email-subject"
                placeholder="client@example.com, …"
                style={{ flex: 1, minWidth: 0 }}
                aria-label="Recipient emails"
                value={to}
                onChange={(ev) => setTo(ev.target.value)}
              />
            </div>
          </div>
          <div className="email-row">
            <span className="email-lbl">Subject</span>
            <input
              className="email-subject"
              value={subject}
              onChange={(ev) => { setSubject(ev.target.value); setDirty(true); }}
            />
          </div>
        </div>

        <div className="email-body">
          <textarea
            className="email-textarea"
            value={bodyText}
            onChange={(ev) => { setBodyText(ev.target.value); setDirty(true); }}
            rows={Math.max(16, bodyText.split("\n").length + 2)}
            spellCheck
            aria-label="Email body"
          />
        </div>
      </div>

      <div className="email-foot-note">
        <Alert size={14} /> This email contains no internal notes, scoring, or competitive analysis. Internal summary stays private.
      </div>
    </div>
  );
}
